import {
  LitElement, html, nothing,
  type CSSResultGroup, type PropertyValues, type TemplateResult,
} from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';

/**
 * uwc-panel
 *
 * A content panel with an optional header, footer, and smooth toggleable
 * collapse/expand behaviour.
 *
 * @attr {string}  header      - Header title text. Overridden by `slot="header"`.
 * @attr {string}  icon        - Bootstrap Icons name shown left of the header title.
 * @attr {boolean} toggleable  - Show a collapse/expand toggle button in the header.
 * @attr {boolean} collapsed   - Whether the content is collapsed. Reflects to attribute.
 * @attr {boolean} no-header   - Hide the header bar entirely.
 *
 * @slot default        - Main content body.
 * @slot header         - Custom header content; replaces the `header` attribute text.
 * @slot icons          - Action icons placed in the header trailing area (before the toggler).
 * @slot footer         - Footer content rendered below the body.
 *
 * @fires uwc-toggle    - Fired when the toggle button is clicked.
 *                        `detail: { collapsed: boolean }`
 * @fires uwc-collapse  - Fired when the panel finishes collapsing.
 * @fires uwc-expand    - Fired when the panel starts expanding.
 *
 * @csspart panel         - The outer wrapper `<div>`.
 * @csspart header        - The header bar `<div>`.
 * @csspart header-icon   - The icon wrapper `<span>`.
 * @csspart title         - The title `<span>`.
 * @csspart actions       - The actions area `<span>`.
 * @csspart toggler       - The collapse toggle `<button>`.
 * @csspart content-wrap  - The animated content wrapper `<div>`.
 * @csspart content       - The inner content `<div>`.
 * @csspart footer        - The footer `<div>`.
 *
 * @cssprop --uwc-panel-bg                  - Panel background. Default: white.
 * @cssprop --uwc-panel-border              - Panel border.
 * @cssprop --uwc-panel-radius              - Panel border radius.
 * @cssprop --uwc-panel-shadow              - Panel box-shadow.
 * @cssprop --uwc-panel-header-padding      - Header padding.
 * @cssprop --uwc-panel-header-bg           - Header background.
 * @cssprop --uwc-panel-header-border       - Header bottom border.
 * @cssprop --uwc-panel-header-color        - Header text colour.
 * @cssprop --uwc-panel-header-font-size    - Header font size.
 * @cssprop --uwc-panel-header-font-weight  - Header font weight.
 * @cssprop --uwc-panel-icon-color          - Header icon colour.
 * @cssprop --uwc-panel-toggler-color       - Toggle button icon colour.
 * @cssprop --uwc-panel-content-padding     - Content area padding.
 * @cssprop --uwc-panel-content-color       - Content text colour.
 * @cssprop --uwc-panel-content-font-size   - Content font size.
 * @cssprop --uwc-panel-footer-padding      - Footer padding.
 * @cssprop --uwc-panel-footer-bg           - Footer background.
 * @cssprop --uwc-panel-footer-border       - Footer top border.
 */
export class UwcPanel extends LitElement {

  static override styles: CSSResultGroup = styles;

  // ── Public properties ──────────────────────────────────────────────────────

  /** Header title text. Use `slot="header"` for richer markup. */
  @property() header = '';

  /** Bootstrap Icons name shown to the left of the header title. */
  @property() icon?: string;

  /** Show a collapse / expand toggle button in the header. */
  @property({ type: Boolean, reflect: true })
  toggleable = false;

  /**
   * Whether the panel content is currently collapsed.
   * Reflects to attribute so `:host([collapsed])` works in CSS.
   */
  @property({ type: Boolean, reflect: true })
  collapsed = false;

  /** Hide the header bar entirely. Content is always visible in this mode. */
  @property({ type: Boolean, reflect: true, attribute: 'no-header' })
  noHeader = false;

  // ── Shadow refs ────────────────────────────────────────────────────────────

  @query('.uwc-panel__content-wrap') private _wrap?: HTMLElement;

  // ── Internal state ─────────────────────────────────────────────────────────

  /** Whether the footer slot has any assigned nodes. */
  @state() private _hasFooter = false;

  /** In-flight WAAPI animation reference for smooth reversal. */
  private _currentAnimation?: Animation;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override firstUpdated() {
    // Apply initial collapsed state without animation.
    if (this._wrap && this.collapsed) {
      this._wrap.style.height   = '0px';
      this._wrap.style.overflow = 'hidden';
    }
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);

    if (!changed.has('collapsed') || !this._wrap) return;

    if (this.collapsed) {
      this._animateClose();
    } else {
      this._animateOpen();
    }
  }

  // ── WAAPI animations ───────────────────────────────────────────────────────

  private _animateOpen() {
    const wrap = this._wrap;
    if (!wrap) return;

    const fromHeight = this._currentAnimation
      ? wrap.getBoundingClientRect().height
      : 0;

    this._currentAnimation?.cancel();
    this._currentAnimation = undefined;

    // Temporarily remove height:0 so scrollHeight is accurate.
    wrap.style.removeProperty('height');
    wrap.style.removeProperty('overflow');
    const toHeight = wrap.scrollHeight;

    const anim = wrap.animate(
      [
        { height: `${fromHeight}px`, overflow: 'hidden' },
        { height: `${toHeight}px`,   overflow: 'hidden' },
      ],
      { duration: this._duration(), easing: 'ease', fill: 'both' },
    );
    this._currentAnimation = anim;

    anim.addEventListener('finish', () => {
      if (this._currentAnimation !== anim) return;
      anim.cancel();
      wrap.style.removeProperty('height');
      wrap.style.removeProperty('overflow');
      this._currentAnimation = undefined;
      emit(this, 'uwc-expand');
    }, { once: true });
  }

  private _animateClose() {
    const wrap = this._wrap;
    if (!wrap) return;

    const fromHeight = wrap.getBoundingClientRect().height;

    this._currentAnimation?.cancel();
    this._currentAnimation = undefined;

    const anim = wrap.animate(
      [
        { height: `${fromHeight}px`, overflow: 'hidden' },
        { height: '0px',             overflow: 'hidden' },
      ],
      { duration: this._duration(), easing: 'ease', fill: 'both' },
    );
    this._currentAnimation = anim;

    anim.addEventListener('finish', () => {
      if (this._currentAnimation !== anim) return;
      anim.cancel();
      wrap.style.height   = '0px';
      wrap.style.overflow = 'hidden';
      this._currentAnimation = undefined;
      emit(this, 'uwc-collapse');
    }, { once: true });
  }

  private _duration(): number {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250;
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  private _onToggle() {
    this.collapsed = !this.collapsed;
    emit(this, 'uwc-toggle', { collapsed: this.collapsed });
  }

  private _onFooterSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._hasFooter = slot.assignedNodes({ flatten: true }).length > 0;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Expand the panel. No-op if already expanded. */
  expand() {
    if (!this.collapsed) return;
    this.collapsed = false;
    emit(this, 'uwc-toggle', { collapsed: false });
  }

  /** Collapse the panel. No-op if already collapsed. */
  collapse() {
    if (this.collapsed) return;
    this.collapsed = true;
    emit(this, 'uwc-toggle', { collapsed: true });
  }

  /** Toggle the panel open/closed. */
  toggle() {
    this._onToggle();
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _renderHeader(): TemplateResult {
    return html`
      <div part="header" class="uwc-panel__header">
        ${this.icon ? html`
          <span part="header-icon" class="uwc-panel__header-icon" aria-hidden="true">
            <uwc-icon name=${this.icon} size="1.25em"></uwc-icon>
          </span>
        ` : nothing}

        <slot name="header">
          <span part="title" class="uwc-panel__title">${this.header}</span>
        </slot>

        <span part="actions" class="uwc-panel__header-actions">
          <slot name="icons"></slot>
        </span>

        ${this.toggleable ? html`
          <button
            type="button"
            part="toggler"
            class="uwc-panel__toggler"
            aria-label=${this.collapsed ? 'Expand panel' : 'Collapse panel'}
            aria-expanded=${!this.collapsed ? 'true' : 'false'}
            aria-controls="uwc-panel-content"
            @click=${this._onToggle}
          >
            <span class="uwc-panel__toggler-icon" aria-hidden="true">
              <uwc-icon name="chevron-down" size="1em"></uwc-icon>
            </span>
          </button>
        ` : nothing}
      </div>
    `;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    return html`
      <div part="panel" class="uwc-panel">
        ${!this.noHeader ? this._renderHeader() : nothing}

        <div
          part="content-wrap"
          class="uwc-panel__content-wrap"
          id="uwc-panel-content"
          role="region"
          aria-labelledby="uwc-panel-header-title"
        >
          <div part="content" class="uwc-panel__content">
            <slot></slot>
          </div>
        </div>

        <div
          part="footer"
          class=${classMap({ 'uwc-panel__footer': true, 'uwc-panel__footer--hidden': !this._hasFooter })}
        >
          <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-panel': UwcPanel; }
}
